import React, { Component } from 'react';
import { array, arrayOf, func, node, number, object, oneOf, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { parseSelectFilterOptions } from '../../../util/search';
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../../util/types';

import { languagesHelper as language } from '../../../helpers/languages';

import { FieldCheckbox } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './SelectMultipleFilter.module.css';

const MAX_ITEMS_LENGTH = process.env.REACT_APP_FILTER_OPTIONS_MAX_LIMIT || 10;

// SelectMultipleFilter doesn't need array mutators since it doesn't require validation.
// TODO: Live edit didn't work with FieldCheckboxGroup
//       There's a mutation problem: formstate.dirty is not reliable with it.
const GroupOfFieldCheckboxes = props => {
  const { id, className, name, options, intl = { intl }, searchOptions, withSearchInput } = props;

  const multiLanguageOptions = options?.map(o => {
    return {
      key: o.key,
      label: language.labelsTranslator(o.key, intl) || o.label,
      isFirst: o.isFirst,
    }
  })

  const fieldCheckboxOptions = !!searchOptions?.length ?
    multiLanguageOptions?.filter(o => o.label.toLowerCase().indexOf(searchOptions.toLowerCase()) !== -1)
    : options

  if (!fieldCheckboxOptions?.length) {
    return (
      <fieldset className={className}>
        <ul className={css.list}>
          <li className={classNames(css.item, css.itemNoResult)}>
            <FormattedMessage id='SelectMultipleFilter.noSearchOptions' />
          </li>
        </ul>
      </fieldset>
    )
  };

  const sortedOptions = fieldCheckboxOptions.sort((a, b) => {
    if (a.isFirst && !b.isFirst) return -1;
    if (!a.isFirst && b.isFirst) return 1;
    return 0;
  });

  const [visibleCount, setVisibleCount] = React.useState(MAX_ITEMS_LENGTH);

  const toggleVisibleCount = () => {
    if (visibleCount === MAX_ITEMS_LENGTH) {
      setVisibleCount(sortedOptions.length);
    } else {
      setVisibleCount(MAX_ITEMS_LENGTH);
    }
  };

  const visibleOptions = sortedOptions?.length > MAX_ITEMS_LENGTH ? sortedOptions.slice(0, visibleCount) : sortedOptions;

  return (
    <fieldset className={className}>
      <ul className={css.list} key={`${name}-list-${visibleOptions?.length}`}>
        {visibleOptions.map((option, index) => {
          const fieldId = `${id}.${option.key}`;
          return (
            <li key={fieldId} className={css.item}>
              <FieldCheckbox
                id={fieldId}
                name={name}
                label={language.labelsTranslator(option.key, intl) || option.label}
                value={option.key}
              />
            </li>
          );
        })}
      </ul>
      {withSearchInput && sortedOptions.length > MAX_ITEMS_LENGTH && (
        <span
          className={css.showMoreButton}
          onClick={toggleVisibleCount}
        >
          {visibleCount === MAX_ITEMS_LENGTH ?
            intl.formatMessage({ id: 'SelectMultipleFilter.showMore' })
            : intl.formatMessage({ id: 'SelectMultipleFilter.showLess' })
          }
        </span>
      )}
    </fieldset>
  );
};

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

// Format URI component's query param: { pub_key: 'has_all:a,b,c' }
const format = (selectedOptions, queryParamName, schemaType, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
  return { [queryParamName]: value };
};

class SelectMultipleFilter extends Component {
  constructor(props) {
    super(props);

    this.state = { searchOptions: '' };

    this.filter = null;
    this.filterContent = null;

    this.positionStyleForContent = this.positionStyleForContent.bind(this);
    this.onChangeSearchOptions = this.onChangeSearchOptions.bind(this);
    this.onClearSearchOptions = this.onClearSearchOptions.bind(this);
  }

  positionStyleForContent() {
    if (this.filter && this.filterContent) {
      // Render the filter content to the right from the menu
      // unless there's no space in which case it is rendered
      // to the left
      const distanceToRight = window.innerWidth - this.filter.getBoundingClientRect().right;
      const labelWidth = this.filter.offsetWidth;
      const contentWidth = this.filterContent.offsetWidth;
      const contentWidthBiggerThanLabel = contentWidth - labelWidth;
      const renderToRight = distanceToRight > contentWidthBiggerThanLabel;
      const contentPlacementOffset = this.props.contentPlacementOffset;

      const offset = renderToRight
        ? { left: contentPlacementOffset }
        : { right: contentPlacementOffset };
      // set a min-width if the content is narrower than the label
      const minWidth = contentWidth < labelWidth ? { minWidth: labelWidth } : null;

      return { ...offset, ...minWidth };
    }
    return {};
  }

  onChangeSearchOptions(e) {
    this.setState({ searchOptions: e.target.value });
  }

  onClearSearchOptions() {
    this.setState({ searchOptions: '' });
  }

  render() {
    const {
      rootClassName,
      className,
      id,
      name,
      label,
      options,
      initialValues,
      contentPlacementOffset,
      onSubmit,
      queryParamNames,
      schemaType,
      searchMode,
      intl,
      showAsPopup,
      withSearchInput,
      ...rest
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);

    const queryParamName = getQueryParamName(queryParamNames);
    const hasInitialValues = !!initialValues && !!initialValues[queryParamName];
    // Parse options from param strings like "has_all:a,b,c" or "a,b,c"
    const selectedOptions = hasInitialValues
      ? parseSelectFilterOptions(initialValues[queryParamName])
      : [];

    const labelForPopup = hasInitialValues
      ? intl.formatMessage(
        { id: 'SelectMultipleFilter.labelSelected' },
        { labelText: label, count: selectedOptions.length }
      )
      : label;

    const labelSelectionForPlain = hasInitialValues
      ? intl.formatMessage(
        { id: 'SelectMultipleFilterPlainForm.labelSelected' },
        { count: selectedOptions.length }
      )
      : '';

    const contentStyle = this.positionStyleForContent();

    // pass the initial values with the name key so that
    // they can be passed to the correct field
    const namedInitialValues = { [name]: selectedOptions };

    const handleSubmit = values => {
      const usedValue = values ? values[name] : values;
      onSubmit(format(usedValue, queryParamName, schemaType, searchMode));
    };

    const { searchOptions } = this.state;

    return showAsPopup ? (
      <FilterPopup
        className={classes}
        rootClassName={rootClassName}
        popupClassName={css.popupSize}
        name={name}
        label={labelForPopup}
        isSelected={hasInitialValues}
        id={`${id}.popup`}
        showAsPopup
        contentPlacementOffset={contentPlacementOffset}
        onSubmit={handleSubmit}
        initialValues={namedInitialValues}
        keepDirtyOnReinitialize
        withSearchInput={withSearchInput}
        onChangeSearchOptions={this.onChangeSearchOptions}
        onClearSearchOptions={this.onClearSearchOptions}
        searchOptions={searchOptions}
        {...rest}
      >
        <GroupOfFieldCheckboxes
          className={css.fieldGroup}
          name={name}
          id={`${id}-checkbox-group`}
          options={options}
          intl={intl}
          searchOptions={searchOptions}
          withSearchInput={withSearchInput}
        />
      </FilterPopup>
    ) : (
      <FilterPlain
        className={className}
        rootClassName={rootClassName}
        label={label}
        labelSelection={labelSelectionForPlain}
        isSelected={hasInitialValues}
        id={`${id}.plain`}
        liveEdit
        contentPlacementOffset={contentStyle}
        onSubmit={handleSubmit}
        initialValues={namedInitialValues}
        withSearchInput={withSearchInput}
        onChangeSearchOptions={this.onChangeSearchOptions}
        onClearSearchOptions={this.onClearSearchOptions}
        searchOptions={searchOptions}
        {...rest}
      >
        <GroupOfFieldCheckboxes
          className={css.fieldGroupPlain}
          name={name}
          id={`${id}-checkbox-group`}
          options={options}
          intl={intl}
          searchOptions={searchOptions}
          withSearchInput={withSearchInput}
        />
      </FilterPlain>
    );
  }
}

SelectMultipleFilter.defaultProps = {
  rootClassName: null,
  className: null,
  initialValues: null,
  contentPlacementOffset: 0,
  searchMode: null,
};

SelectMultipleFilter.propTypes = {
  rootClassName: string,
  className: string,
  id: string.isRequired,
  name: string.isRequired,
  queryParamNames: arrayOf(string).isRequired,
  label: node.isRequired,
  onSubmit: func.isRequired,
  options: array.isRequired,
  searchMode: oneOf(['has_all', 'has_any']),
  schemaType: oneOf([SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM]).isRequired,
  initialValues: object,
  contentPlacementOffset: number,

  // form injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(SelectMultipleFilter);
